import EmberService, { service } from '@ember/service';

import type { Level as RollbarLogLevel } from 'rollbar';

import type { Service } from 'webapp-types';
import { copyObject, safeJSONStringify } from 'webapp/utils/common';
import type { LogData } from 'webapp/utils/sentry';
import { initSentry, reportToSentry } from 'webapp/utils/sentry';

export enum Severity {
	CRITICAL = 'critical',
	DEBUG = 'debug',
	ERROR = 'error',
	INFO = 'info',
	WARNING = 'warning',
}

// Rollbar still gets every severity; only these also go to Sentry
const SENTRY_SEVERITIES: string[] = [Severity.CRITICAL, Severity.ERROR];

export default class LoggerService extends EmberService {
	@service('navigation') declare navigationService: Service.Navigation;
	@service('request') declare requestService: Service.Request;

	spaceId: number | null = null;

	private reportedErrors: string[] = [];
	private offlineTimeout: ReturnType<typeof setTimeout> | null = null;
	private wasOfflineRecently = false;

	bindGLobalErrorHandlers() {
		initSentry();

		// compatible with window.onerror
		// https://developer.mozilla.org/en/docs/Web/API/GlobalEventHandlers/onerror
		const globalErrorHandler: OnErrorEventHandler = (
			message: string | Event,
			url?: string,
			line?: number,
			col?: number,
			errorObj?: Error,
		) => {
			message = String((message as string) || '');
			const scriptError = message.toLowerCase().includes('script error');
			if (!scriptError) {
				if (message) {
					this.logError(message, errorObj || { url, line, col });
				} else {
					// should not really happen
					this.logError('Generic error', errorObj);
				}
			}
		};

		// https://developer.mozilla.org/en/docs/Web/API/GlobalEventHandlers/onerror
		window.onerror = globalErrorHandler;

		window.addEventListener('unhandledrejection', (e) => {
			// unhandled promise rejections

			if (e.reason instanceof Error) {
				globalErrorHandler(e.reason.message, '', -1, -1, e.reason);
			} else {
				// skip errors from browser extensions (e.g. "safari-extension://", don't know how chrome handles it)
				const reason = e.reason as { sourceURL?: string } | null | undefined; // Safari sets sourceURL on its rejections
				if (reason?.sourceURL && !reason.sourceURL.includes('extension://')) {
					this.logError('Unhandled promise rejection', {
						reason: safeJSONStringify(e.reason),
					});
				}
			}

			e.preventDefault(); // suppress logging to console
		});

		window.addEventListener('online', this.connectivityCheck.bind(this));
		window.addEventListener('offline', this.connectivityCheck.bind(this));
	}

	private _skipErrorMessage(msg: string, extradata: LogData): boolean {
		let skip = false;

		if (/*msg === 'TransitionAborted' || */ msg.includes('The adapter')) {
			skip = true;
		}

		if (msg.startsWith('source=file:///') || msg === 'unknown') {
			// skip local plugin errors
			skip = true;
		}

		if (msg.includes('docs-homescreen-gb-container')) {
			// some embedded google docs shit
			skip = true;
		}

		if (msg.includes('getRootNode is not a function')) {
			// old browser? was too much spam https://rollbar.com/databox/Webapp/items/6964
			skip = true;
		}

		if (msg.includes('ResizeObserver loop')) {
			// "ResizeObserver loop completed with undelivered notifications" / "ResizeObserver loop limit exceeded"
			// benign browser warning (recovers on next frame), not actionable; also fires from 3rd-party libs
			skip = true;
		}

		if (msg.indexOf('Could not find module `ember-load-initializers`') > 1) {
			// old browser https://rollbar.com/databox/Webapp/items/14114
			skip = true;
		}

		if (extradata?.stack) {
			// https://rollbar.com/databox/Webapp/items/15288
			if (extradata.stack.includes('cdn.segment.com')) {
				skip = true;
			}

			// https://rollbar.com/databox/Webapp/items/12225/
			if (extradata.stack.includes('cloudfront.net/js/profitwell.js')) {
				skip = true;
			}

			if (!skip) {
				const stack = extradata.stack.split('\n');

				if (stack.length === 1) {
					// https://rollbar.com/databox/Webapp/items/15287
					// https://rollbar.com/databox/Webapp/items/15285/
					// and similar
					if (stack[0]!.includes('<anonymous>')) {
						skip = true;
					}
					// https://rollbar.com/databox/Webapp/items/12744
					// https://rollbar.com/databox/Webapp/items/14523/
					// and similar
					if (stack[0]!.includes('SyntaxError')) {
						skip = true;
					}
				}
			}
		}

		return skip;
	}

	private _isErrorAlreadyReported(msg: string, extradata: LogData): boolean | void {
		let errorString = msg;

		try {
			// to avoid TypeError: Converting circular structure to JSON
			errorString += JSON.stringify(extradata);
		} catch (_e) {
			// skip
		}

		if (this.reportedErrors.includes(errorString)) {
			return true;
		}

		this.reportedErrors.push(errorString);

		setTimeout(() => {
			this.reportedErrors = this.reportedErrors.filter((e) => e !== errorString);
		}, 180 * 1000); // 3 min
	}

	logError(msg: string, logData?: object | null, severity: Severity = Severity.ERROR): boolean {
		const extradata = (logData || {}) as LogData; // the same object: spaceId / activeToken are added to it

		if (
			![Severity.CRITICAL, Severity.WARNING, Severity.INFO, Severity.DEBUG].includes(severity)
		) {
			severity = Severity.ERROR;
		}

		if (this.spaceId) {
			extradata.spaceId = this.spaceId;
		}

		if (this.navigationService.isLoadedFromSnapshooter) {
			extradata.activeToken = this.requestService.wallToken;
		}

		const hasRollbar = typeof window.Rollbar !== 'undefined';
		const skipLogging =
			(!hasRollbar && !window.sentryConfig) ||
			this._skipErrorMessage(msg, extradata) ||
			(severity !== Severity.CRITICAL && this._isErrorAlreadyReported(msg, extradata)); // throttle non-critical

		const reportFn = (severity: string, msg: string, extradata: LogData) => {
			if (!skipLogging) {
				if (hasRollbar) {
					window.Rollbar[severity as RollbarLogLevel](msg, extradata);
				}
				if (SENTRY_SEVERITIES.includes(severity)) {
					reportToSentry(
						msg,
						extradata,
						severity === Severity.CRITICAL ? 'fatal' : 'error',
					);
				}
			}

			if (window.LOGIT) {
				// eslint-disable-next-line no-console
				console.error(msg, extradata);
			}
		};

		if (msg === '(Request) error' || msg === '(Request) Timeout') {
			// this could be an internet problem - delay logging it for 5s so maybe wasOfflineRecently could kick in
			setTimeout(() => {
				if (!this.wasOfflineRecently) {
					reportFn(severity, msg, extradata || {});
				}
			}, 5000);
		} else {
			reportFn(severity, msg, extradata);
		}

		return true; // suppress standard error dialog
	}

	logSocketEvent(eventName: string, data?: object, channelName?: string): void {
		let msg = eventName;
		if (channelName) {
			msg += ` [${channelName}]`;
		}
		if (data) {
			this.logToConsole(msg, copyObject(data));
		} else {
			this.logToConsole(msg);
		}
	}

	logToConsole(...args: unknown[]): void {
		let argsToLog = args;
		let method: 'log' | 'error' = 'log';
		if (args[0] === 'error' && args.length > 1) {
			argsToLog = args.slice(1);
			method = 'error';
		}
		if (window.LOGIT) {
			// eslint-disable-next-line no-console
			console[method](...argsToLog);
		} else if (window.env === 'test') {
			// eslint-disable-next-line no-console
			console[method](JSON.stringify(argsToLog));
		}
	}

	prepareJSONForLogging(json?: unknown): string {
		if (!json) {
			return '';
		}

		// const copy = copyObject(json);
		// if (copy.accessToken) {
		// 	copy.accessToken = '***';
		// }
		// return JSON.stringify(copy);

		return JSON.stringify(json);
	}

	private connectivityCheck(): void {
		if (navigator.onLine) {
			if (this.offlineTimeout) {
				clearTimeout(this.offlineTimeout);
			}
			this.offlineTimeout = setTimeout(() => {
				if (navigator.onLine) {
					this.wasOfflineRecently = false;
				}
			}, 5000); // wasOfflineRecently flag lasts for 5s more after connecting again
		} else {
			this.wasOfflineRecently = true;
		}
	}
}

declare module '@ember/service' {
	interface Registry {
		logger: LoggerService;
	}
}
